import * as preact from "preact";

import * as accountModal from "accountModal";
import * as editCardDialog from "editCardDialog";
import UserModel from "models/UserModel";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import AskAIView from "views/AskAIView";
import ExplanationView from "views/ExplanationView";
import PracticePlayTTSView from "views/PracticePlayTTSView";

interface Props {
  user: UserModel;
  userWord: UserWordModel;
  context: string;
  monolingualMode: boolean;
}

interface State {
  reachedLimit: boolean;
  fetched: boolean;
  definition?: string;
  englishTranslation?: string;
}

class SmartDictionaryView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      reachedLimit: false,
      fetched: false,
    };

    this.props.user.on("change", this.onChange);
    this.props.userWord.on("change", this.onChange);
  }

  onChange = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    this.props.user.off("change", this.onChange);
    this.props.userWord.off("change", this.onChange);
  }

  upgrade() {
    accountModal.show({
      model: this.props.user,
      title: "explanationsLimit",
      messageHtml:
        "<div>" +
        "<h2>Upgrade for more than " +
        subscriptionPlans.permissions["free"].definitions +
        " explanations per day</h2>" +
        "<p>Really understand what you're reading with unlimited AI-based " +
        "context-aware explanations!</p>" +
        "</div>",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  toggleLanguage() {
    this.props.user.setSmartDictionaryInFirstLanguage(
      this.props.userWord.get("language"),
      !this.props.user.isSmartDictionaryInFirstLanguage(
        this.props.userWord.get("language")
      )
    );

    // Reset the fetched state
    this.setState({
      definition: "",
      fetched: false,
      reachedLimit: false,
    });
  }

  render() {
    const language = this.props.userWord.get("language");
    const query = this.props.userWord.get("word");
    const translation = this.props.userWord.get("translation");
    const alternativeTranslations = this.props.userWord.get(
      "alternativeTranslations"
    );
    const limit = subscriptionPlans.permissions["free"].definitions;
    const { reachedLimit } = this.state;

    const toggleLinkText =
      !this.state.fetched || this.state.reachedLimit
        ? undefined
        : `Switch explanations to ${
            this.props.user.isSmartDictionaryInFirstLanguage(language)
              ? languageMap.map[language].english
              : languageMap.map[models.user.get("firstLanguage")].english
          }`;

    return (
      <div class="smartDictionaryContent">
        <h2 class="smartDictionaryHeader">
          {this.state.fetched ? (
            <button class="editWordButton" onClick={() => this.editWord()}>
              <i class="icon-pencil"></i> Edit{" "}
              {this.props.userWord.isPhrase() ? "phrase" : "word"}
            </button>
          ) : null}
          <PracticePlayTTSView text={query} language={language} />
          {query}
          {translation && !this.props.monolingualMode ? (
            <>
              {" "}
              <span style={{ fontWeight: 300 }}>-</span>{" "}
              {alternativeTranslations.length === 0 ? (
                <span class="smartDictionaryTranslation">{translation}</span>
              ) : (
                [translation, ...alternativeTranslations].map(
                  (thisTranslation, index) => (
                    <span class="smartDictionaryTranslation">
                      {index !== 0 ? " / " : ""}
                      {thisTranslation}
                      <button
                        class="removeButton"
                        onClick={() => {
                          const oldTranslations = [
                            translation,
                            ...alternativeTranslations,
                          ];
                          const newTranslations = [
                            ...oldTranslations.slice(0, index),
                            ...oldTranslations.slice(index + 1),
                          ];
                          this.props.userWord.save(
                            {
                              translation: newTranslations[0],
                              alternativeTranslations: newTranslations.slice(1),
                            },
                            { patch: true }
                          );
                        }}
                      >
                        <i class="icon icon-remove-sign"></i>
                      </button>
                    </span>
                  )
                )
              )}
            </>
          ) : null}
        </h2>
        {reachedLimit && (
          <p>
            You've reached your daily limit of {limit} context-aware
            explanations. Get unlimited explanations by{" "}
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.upgrade();
              }}
            >
              upgrading to premium
            </a>
            .
          </p>
        )}
        <ExplanationView
          word={query}
          context={this.props.context}
          language={language}
          showInFirstLanguage={this.props.user.isSmartDictionaryInFirstLanguage(
            language
          )}
          cacheOnly={false}
          existingTranslations={
            this.props.monolingualMode
              ? []
              : [translation, ...alternativeTranslations]
          }
          onExplanationFetched={() => {
            if (!this.state.fetched) {
              this.setState({ fetched: true });
            }
          }}
          onSelectTranslation={
            this.props.monolingualMode
              ? undefined
              : (selectedTranslation) =>
                  this.addTranslation(selectedTranslation)
          }
        />
        {toggleLinkText && (
          <p>
            <a
              class="toggleLink"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.toggleLanguage();
              }}
            >
              {toggleLinkText}
            </a>
          </p>
        )}
        {this.state.fetched && this.props.user.hasFeature("askAI") ? (
          <AskAIView
            userWord={this.props.userWord}
            context={this.props.context}
          />
        ) : null}
      </div>
    );
  }

  addTranslation(translation: string) {
    this.props.userWord.save(
      {
        alternativeTranslations: [
          ...this.props.userWord.get("alternativeTranslations"),
          translation,
        ],
      },
      { patch: true }
    );
  }

  editWord() {
    editCardDialog.show(this.props.userWord, {
      title: "Edit Word / Phrase",
    });
  }
}

export default SmartDictionaryView;
